import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Repeat } from 'lucide-react';
import { commerceAPI } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Field, inputCls, Toggle, ConfirmDialog, rewardLabel, fmtCve, fmtDate } from './shared';

const EMPTY = { name: '', description: '', trigger: 'nth_purchase', threshold: 3, repeat: false, reward_kind: 'percent', reward_value: 10, active: true };
const TIER_STYLE = { novo: 'bg-slate-100 text-slate-700', regular: 'bg-blue-100 text-blue-800', fiel: 'bg-amber-100 text-amber-800', premium: 'bg-purple-100 text-purple-800' };

const RuleForm = ({ open, initial, onClose, onSaved, t }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  useEffect(() => { if (open) setF(initial ? { ...EMPTY, ...initial, description: initial.description || '' } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  const save = async () => {
    setSaving(true);
    const body = { ...f, description: f.description || null, threshold: f.trigger === 'republish' ? 0 : Number(f.threshold) };
    try {
      if (isEdit) { const { id, created_at, ...patch } = body; await commerceAPI.updateRule(initial.id, patch); }
      else await commerceAPI.createRule(body);
      toast.success(t('adminCommerce.saved')); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || t('adminCommerce.saveError')); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="rule-form">
        <DialogHeader><DialogTitle>{isEdit ? t('adminCommerce.loyalty.edit') : t('adminCommerce.loyalty.new')}</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t('adminCommerce.loyalty.name')} className="sm:col-span-2"><input className={inputCls} value={f.name} onChange={set('name')} placeholder="3ª compra com 10% desconto" data-testid="rule-name" /></Field>
          <Field label={t('adminCommerce.loyalty.trigger')} className="sm:col-span-2">
            <select className={inputCls} value={f.trigger} onChange={set('trigger')} data-testid="rule-trigger">
              <option value="nth_purchase">{t('adminCommerce.loyalty.triggers.nth_purchase')}</option>
              <option value="total_spent">{t('adminCommerce.loyalty.triggers.total_spent')}</option>
              <option value="jobs_published">{t('adminCommerce.loyalty.triggers.jobs_published')}</option>
              <option value="republish">{t('adminCommerce.loyalty.triggers.republish')}</option>
            </select>
          </Field>
          {f.trigger !== 'republish' && (
            <Field label={t('adminCommerce.loyalty.threshold')} hint={t(`adminCommerce.loyalty.thresholdHint.${f.trigger}`)}><input type="number" min="1" className={inputCls} value={f.threshold} onChange={set('threshold')} data-testid="rule-threshold" /></Field>
          )}
          {f.trigger === 'nth_purchase' && (
            <div className="flex items-end"><Toggle checked={f.repeat} onChange={(v) => setF({ ...f, repeat: v })} label={t('adminCommerce.loyalty.repeat')} testId="rule-repeat-toggle" /></div>
          )}
          <Field label={t('adminCommerce.loyalty.reward')}>
            <select className={inputCls} value={f.reward_kind} onChange={set('reward_kind')} data-testid="rule-reward-kind">
              <option value="percent">{t('adminCommerce.kinds.percent')}</option>
              <option value="fixed">{t('adminCommerce.kinds.fixed')}</option>
              <option value="bonus_credits">{t('adminCommerce.kinds.bonus_credits')}</option>
            </select>
          </Field>
          <Field label={t('adminCommerce.value')} hint={t(`adminCommerce.valueHint.${f.reward_kind}`)}><input type="number" min="1" className={inputCls} value={f.reward_value} onChange={set('reward_value')} data-testid="rule-reward-value" /></Field>
          <Field label={t('adminCommerce.description')} hint={t('adminCommerce.loyalty.descriptionHint')} className="sm:col-span-2"><textarea className={`${inputCls} h-16`} value={f.description} onChange={set('description')} /></Field>
          <div><Toggle checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={f.active ? t('adminCommerce.active') : t('adminCommerce.inactive')} testId="rule-active-toggle" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('adminCommerce.cancel')}</Button>
          <Button onClick={save} disabled={saving || !f.name} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rule-save-btn">{t('adminCommerce.save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RuleRow = ({ r, onEdit, onToggle, onDelete, t }) => (
  <Card className={`p-4 flex flex-col md:flex-row md:items-center gap-4 ${r.active ? '' : 'opacity-60'}`} data-testid={`rule-row-${r.id}`}>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-slate-900">{r.name}</h3>
        <span className="text-sm font-bold text-emerald-700">{rewardLabel(r.reward_kind, r.reward_value, t)}</span>
        {r.repeat && <span className="inline-flex items-center gap-1 text-[11px] font-bold uppercase px-2 py-0.5 rounded-full bg-blue-100 text-blue-800"><Repeat className="h-3 w-3" />{t('adminCommerce.loyalty.repeatBadge')}</span>}
        {!r.active && <span className="text-[11px] font-bold uppercase text-rose-700">{t('adminCommerce.inactive')}</span>}
      </div>
      <p className="text-sm text-slate-600 mt-1">{t(`adminCommerce.loyalty.ruleText.${r.trigger}`, { threshold: r.trigger === 'total_spent' ? fmtCve(r.threshold) : r.threshold })}</p>
    </div>
    <div className="flex gap-2 flex-wrap">
      <Button size="sm" variant="outline" onClick={() => onEdit(r)} data-testid={`rule-edit-${r.id}`}><Pencil className="h-4 w-4 mr-1" />{t('adminCommerce.edit')}</Button>
      <Button size="sm" variant="outline" onClick={() => onToggle(r)} data-testid={`rule-toggle-${r.id}`}>{r.active ? t('adminCommerce.deactivate') : t('adminCommerce.activate')}</Button>
      <Button size="sm" variant="outline" className="text-rose-700 border-rose-200 hover:bg-rose-50" onClick={() => onDelete(r)} data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  </Card>
);

const TiersTable = ({ stats, t }) => (
  <Card className="p-5 mt-8" data-testid="loyalty-tiers">
    <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
      <h3 className="font-semibold text-slate-900">{t('adminCommerce.loyalty.tiersTitle')}</h3>
      <div className="flex gap-2 flex-wrap">
        {Object.entries(stats?.tier_counts || {}).map(([k, v]) => (
          <span key={k} className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${TIER_STYLE[k]}`} data-testid={`tier-count-${k}`}>{t(`loyalty.tier.${k}`)} · {v}</span>
        ))}
      </div>
    </div>
    <p className="text-xs text-slate-500 mb-3">{t('adminCommerce.loyalty.tiersHint')}</p>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b"><th className="py-2">{t('adminCommerce.employer')}</th><th>{t('adminCommerce.loyalty.tier')}</th><th className="text-right">{t('adminCommerce.loyalty.purchases')}</th><th className="text-right">{t('adminCommerce.loyalty.spent')}</th><th className="text-right">{t('adminCommerce.loyalty.last')}</th></tr></thead>
        <tbody>
          {(stats?.top_employers || []).map((e) => (
            <tr key={e.employer_id} className="border-b last:border-0" data-testid={`tier-row-${e.employer_id}`}>
              <td className="py-2"><div className="font-medium text-slate-900">{e.employer_company || '—'}</div><div className="text-xs text-slate-500">{e.employer_email}</div></td>
              <td><span className={`text-[11px] font-bold uppercase px-2 py-0.5 rounded-full ${TIER_STYLE[e.tier]}`}>{t(`loyalty.tier.${e.tier}`)}</span></td>
              <td className="text-right">{e.count}</td>
              <td className="text-right font-semibold">{fmtCve(e.total_cve)}</td>
              <td className="text-right text-slate-500">{fmtDate(e.last_at)}</td>
            </tr>
          ))}
          {!(stats?.top_employers || []).length && <tr><td colSpan={5} className="py-6 text-center text-slate-500">{t('adminCommerce.noData')}</td></tr>}
        </tbody>
      </table>
    </div>
  </Card>
);

export const LoyaltyTab = () => {
  const { t } = useTranslation();
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const load = () => Promise.all([commerceAPI.listRules(), commerceAPI.stats()])
    .then(([a, b]) => { setRules(a.data); setStats(b.data); }).catch(() => toast.error(t('adminCommerce.loadError')));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (r) => { await commerceAPI.updateRule(r.id, { active: !r.active }); load(); };
  const del = async () => { await commerceAPI.deleteRule(toDelete.id); setToDelete(null); toast.success(t('adminCommerce.loyalty.deleted')); load(); };
  return (
    <div data-testid="loyalty-tab">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-slate-600 max-w-xl">{t('adminCommerce.loyalty.intro')}</p>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="rule-new-btn"><Plus className="h-4 w-4 mr-1" />{t('adminCommerce.loyalty.new')}</Button>
      </div>
      {rules.length === 0 && <Card className="p-8 text-center text-slate-500" data-testid="rules-empty">{t('adminCommerce.loyalty.empty')}</Card>}
      <div className="space-y-3">
        {rules.map((r) => <RuleRow key={r.id} r={r} t={t} onEdit={(x) => { setEditing(x); setFormOpen(true); }} onToggle={toggle} onDelete={setToDelete} />)}
      </div>
      <TiersTable stats={stats} t={t} />
      <RuleForm open={formOpen} initial={editing} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); load(); }} t={t} />
      <ConfirmDialog open={Boolean(toDelete)} title={t('adminCommerce.loyalty.deleteTitle')} body={t('adminCommerce.loyalty.deleteBody', { name: toDelete?.name })} onCancel={() => setToDelete(null)} onConfirm={del} confirmLabel={t('adminCommerce.delete')} cancelLabel={t('adminCommerce.cancel')} testId="rule-delete-dialog" />
    </div>
  );
};
